import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ClipboardIcon,
  EyeIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import IndividualResponsesModal from './IndividualResponsesModal';
import CodeBlock from './CodeBlock';
import CopyableBlockquote from './CopyableBlockquote';
import TableDisplay from './TableDisplay';
import FileAttachment from './FileAttachment';

interface MessageAttachment {
  id: number;
  original_filename: string;
  mime_type: string;
  file_size: number;
}

interface ChatMessageProps {
  type: 'user' | 'assistant';
  content: string;
  turnId?: number;
  chatId?: number;
  cost?: number;
  time?: number;
  attachments?: MessageAttachment[];
}

export default function ChatMessage({
  type,
  content,
  turnId,
  chatId,
  cost,
  time,
  attachments,
}: ChatMessageProps) {
  const [copied, setCopied] = useState(false);
  const [showResponses, setShowResponses] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={`py-6 px-4 ${
        type === 'assistant' ? 'bg-surface-800/50' : ''
      }`}
    >
      <div className="max-w-3xl mx-auto">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
              type === 'user' ? 'bg-primary-600' : 'bg-surface-600'
            }`}
          >
            <span className="text-sm font-medium text-white">
              {type === 'user' ? 'U' : 'Q'}
            </span>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Attachment indicators for user messages */}
            {type === 'user' && attachments && attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {attachments.map((att) => (
                  <FileAttachment
                    key={att.id}
                    filename={att.original_filename}
                    fileSize={att.file_size}
                    mimeType={att.mime_type}
                  />
                ))}
              </div>
            )}
            <div className="prose max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  code({ node, inline, className, children, ...props }: any) {
                    const match = /language-(\w+)/.exec(className || '');
                    const codeString = String(children).replace(/\n$/, '');

                    // Use CodeBlock for fenced code blocks with language
                    if (!inline && match) {
                      return <CodeBlock language={match[1]}>{codeString}</CodeBlock>;
                    }

                    // For inline code, use regular code element
                    return (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  },
                  blockquote({ children }: any) {
                    return <CopyableBlockquote>{children}</CopyableBlockquote>;
                  },
                  table({ children }: any) {
                    return <TableDisplay>{children}</TableDisplay>;
                  },
                }}
              >
                {content}
              </ReactMarkdown>
            </div>

            {/* Actions for assistant messages */}
            {type === 'assistant' && (
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-surface-700">
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1 text-sm text-surface-400 hover:text-surface-200 transition-colors"
                >
                  {copied ? (
                    <CheckIcon className="w-4 h-4" />
                  ) : (
                    <ClipboardIcon className="w-4 h-4" />
                  )}
                  {copied ? 'Copied' : 'Copy'}
                </button>

                {turnId && chatId && (
                  <button
                    onClick={() => setShowResponses(true)}
                    className="flex items-center gap-1 text-sm text-surface-400 hover:text-surface-200 transition-colors"
                  >
                    <EyeIcon className="w-4 h-4" />
                    View Individual Responses
                  </button>
                )}

                {cost !== undefined && (
                  <span className="text-sm text-surface-500">
                    ${cost.toFixed(4)}
                  </span>
                )}

                {time !== undefined && (
                  <span className="text-sm text-surface-500">
                    {(time / 1000).toFixed(1)}s
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Individual Responses Modal */}
      {turnId && chatId && (
        <IndividualResponsesModal
          isOpen={showResponses}
          onClose={() => setShowResponses(false)}
          chatId={chatId}
          turnId={turnId}
        />
      )}
    </div>
  );
}
